import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid2 as Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import RepeatIcon from "@mui/icons-material/Repeat";
import moment from "moment";
import { useAtom } from "jotai";
import { useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import {
  useFetchAccountLocationsQuery,
  useFetchOrganizationInfoQuery,
} from "../../../../api";
import { selectedOrgIdAtom } from "../../../../atoms";
import { DatePicker, SelectBox, TextField } from "../../../common";
import {
  CALENDAR_DATE_FORMAT,
  CALENDAR_TIME_SLOTS,
  CREW_ROLE_LABELS,
  JOB_CUSTOM_OPTION,
  JOB_DURATION_DROPDOWN,
  JOB_DEFAULT_EVENT_TYPE,
  JOB_DURATION_INPUT_FORMAT,
  JOB_EVENT_TYPE_DROPDOWN,
  JOB_STATUS_DROPDOWN,
  JOB_TIME_FORMAT,
  JOB_TIME_INPUT_FORMAT,
  JOB_TYPE_FIELDS,
  JOB_UNASSIGNED_TRUCK,
  RECURRENCE_CUSTOM_OPTION,
  RECURRENCE_UNSUPPORTED_MESSAGE,
  SCHEDULE_CATEGORY_OPTIONS,
} from "../../../../constants";
import {
  FieldRequirement,
  JobRecurrence,
  JobStatus,
  JobType,
  RecurrenceFrequency,
  ScheduleCategory,
} from "../../../../enums";
import { JobModel, LocationModel } from "../../../../models";
import {
  AccountBasicInfo,
  CalendarSlot,
  CrewDay,
  CrewMember,
  DropdownItem,
  Job,
  JobFormData,
  ModalForm,
  RecurrenceRule,
  Truck,
} from "../../../../types";
import { getCountryConfig, theme } from "../../../../utils";
import { jobFormSchema } from "../../../../validations";
import { useValidateForm } from "../../../../hooks";
import { CustomRecurrenceDialog } from "./CustomRecurrenceDialog";
import { CustomTimeField } from "./CustomTimeField";

type JobFormProps = ModalForm<
  Job,
  Job,
  {
    slot: CalendarSlot;
    jobs: Job[];
    accounts: AccountBasicInfo[];
    accountDropdown: DropdownItem[];
    trucks: Truck[];
    crew: CrewMember[];
  }
>;

export const JobForm = ({
  id,
  defaultValues: job,
  handleEdit,
  slot,
  jobs,
  accounts,
  accountDropdown,
  trucks,
  crew,
}: JobFormProps) => {
  const [queryParams] = useSearchParams();
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const fetchOrganizationInfoQuery = useFetchOrganizationInfoQuery(
    selectedOrgId,
    { enabled: selectedOrgId > 0 },
  );
  const { stateLabel, zipLabel, statesDropdown } = getCountryConfig(
    fetchOrganizationInfoQuery.data?.country,
  );
  const defaultValues: JobFormData = {
    type: job?.type ?? JobType.Job,
    title: job?.title ?? "",
    accountId: job
      ? (job.accountId ?? 0)
      : Number(queryParams.get("accountId")) || 0,
    address: job?.address ?? "",
    ...LocationModel.parseAddressLabel(job?.address ?? "", statesDropdown),
    date: job ? moment(job.date, CALENDAR_DATE_FORMAT) : slot.date,
    startTime: job?.startTime ?? slot.startTime,
    durationMinutes:
      job?.durationMinutes ?? Number(JOB_DURATION_DROPDOWN[1].value),
    truckId: job
      ? String(job.truckId ?? JOB_UNASSIGNED_TRUCK)
      : String(slot.truckId ?? JOB_UNASSIGNED_TRUCK),
    status: job?.status ?? JobStatus.Scheduled,
    recurrence: job?.recurrence ?? JobRecurrence.None,
    recurrenceInterval: job?.recurrenceInterval ?? NaN,
    notes: job?.notes ?? "",
  };
  const { register, control, handleSubmit, errors, getValues, setValue } =
    useValidateForm<JobFormData>(jobFormSchema, defaultValues);
  const values = useWatch({ control }) as JobFormData;
  const category = JobModel.isEvent({ type: values.type ?? defaultValues.type })
    ? ScheduleCategory.Event
    : ScheduleCategory.Job;

  const handleCategoryChange = (nextCategory: ScheduleCategory) => {
    if (job || nextCategory === category) return;

    setValue(
      "type",
      nextCategory === ScheduleCategory.Job
        ? JobType.Job
        : JOB_DEFAULT_EVENT_TYPE,
    );
  };
  const getInitialCrewIds = () =>
    job?.crew.map((member) => member.id) ?? (slot.crewId ? [slot.crewId] : []);
  const [crewIds, setCrewIds] = useState<number[]>(getInitialCrewIds);

  useEffect(() => {
    setCrewIds(getInitialCrewIds());
  }, [job?.id, slot.crewId]);

  const [isCrewMissing, setIsCrewMissing] = useState(false);
  const references = { accounts, trucks, crew };
  const fields = JOB_TYPE_FIELDS[values.type ?? defaultValues.type];
  const isShown = (rule: FieldRequirement) => rule !== FieldRequirement.Hidden;
  const isRequired = (rule: FieldRequirement) =>
    rule === FieldRequirement.Required;
  const selectedDay = values.date?.isValid() ? values.date : null;
  const [isCustomStartTime, setIsCustomStartTime] = useState(
    () =>
      !CALENDAR_TIME_SLOTS.some(
        (slot) => slot.value === defaultValues.startTime,
      ),
  );
  const [isCustomDuration, setIsCustomDuration] = useState(
    () =>
      !JOB_DURATION_DROPDOWN.some(
        (item) => item.value === defaultValues.durationMinutes,
      ),
  );
  const startTime = values.startTime ?? defaultValues.startTime;
  const durationMinutes = Number(
    values.durationMinutes ?? defaultValues.durationMinutes,
  );
  const startTimeDropdown: DropdownItem[] = [
    ...CALENDAR_TIME_SLOTS,
    ...(CALENDAR_TIME_SLOTS.some((slot) => slot.value === startTime)
      ? []
      : [{ key: JobModel.getTimeLabel(startTime), value: startTime }]),
    { key: "Custom…", value: JOB_CUSTOM_OPTION },
  ];
  const durationDropdown: DropdownItem[] = [
    ...JOB_DURATION_DROPDOWN,
    ...(JOB_DURATION_DROPDOWN.some((item) => item.value === durationMinutes)
      ? []
      : [
          {
            key: JobModel.getDurationLabel(durationMinutes),
            value: durationMinutes,
          },
        ]),
    { key: "Custom…", value: JOB_CUSTOM_OPTION },
  ];

  const handleStartTimeChange = (value: string) => {
    if (value === JOB_CUSTOM_OPTION) {
      setIsCustomStartTime(true);
      return;
    }

    setValue("startTime", value, { shouldValidate: true });
  };

  const handleDurationChange = (value: string | number) => {
    if (value === JOB_CUSTOM_OPTION) {
      setIsCustomDuration(true);
      return;
    }

    setValue("durationMinutes", Number(value), { shouldValidate: true });
  };
  const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule | null>(
    () =>
      JobModel.buildRecurrenceRule(
        defaultValues.recurrence,
        job?.recurrenceInterval ?? null,
        defaultValues.date,
      ),
  );
  const [isCustomRecurrenceOpen, setIsCustomRecurrenceOpen] = useState(false);
  const [isRecurrenceUnsupported, setIsRecurrenceUnsupported] = useState(false);
  const [isTruckRecurring, setIsTruckRecurring] = useState(
    job?.isTruckRecurring ?? true,
  );
  const [oneTimeCrewIds, setOneTimeCrewIds] = useState<number[]>(
    () =>
      job?.crew
        .filter((member) => member.isRecurring === false)
        .map((member) => member.id) ?? [],
  );
  const isRepeatingJob = job ? job.seriesId !== null : recurrenceRule !== null;
  const assignmentRecurrence = isRepeatingJob
    ? { isTruckRecurring, oneTimeCrewIds }
    : undefined;

  const toggleCrewRecurrence = (id: number) =>
    setOneTimeCrewIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  const recurrenceDate = selectedDay ?? defaultValues.date;
  const recurrenceOptions = JobModel.buildRecurrenceOptions(recurrenceDate);
  const recurrenceLabel = JobModel.getRecurrenceRuleLabel(
    recurrenceRule,
    recurrenceDate,
  );
  const recurrenceDropdown: DropdownItem[] = [
    ...recurrenceOptions.map((option) => ({
      key: option.label,
      value: option.label,
    })),
    ...(recurrenceOptions.some((option) => option.label === recurrenceLabel)
      ? []
      : [{ key: recurrenceLabel, value: recurrenceLabel }]),
    { key: "Custom…", value: RECURRENCE_CUSTOM_OPTION },
  ];

  useEffect(() => {
    if (!selectedDay) return;

    setRecurrenceRule((prev) =>
      prev?.frequency === RecurrenceFrequency.Week && prev.weekDays.length === 1
        ? { ...prev, weekDays: [selectedDay.day()] }
        : prev,
    );
  }, [selectedDay?.format(CALENDAR_DATE_FORMAT)]);

  const updateRecurrenceRule = (rule: RecurrenceRule | null) => {
    setIsRecurrenceUnsupported(false);
    setRecurrenceRule(rule);
  };

  const handleRecurrenceChange = (value: string) => {
    if (value === RECURRENCE_CUSTOM_OPTION) {
      setIsCustomRecurrenceOpen(true);
      return;
    }

    const option = recurrenceOptions.find((item) => item.label === value);
    if (option) updateRecurrenceRule(option.rule);
  };

  const conflicts = selectedDay
    ? JobModel.getJobConflicts(
        JobModel.buildJob(values, crewIds, references, job),
        jobs,
        trucks,
        crew,
      )
    : [];
  const accountId = Number(values.accountId ?? defaultValues.accountId);
  const address = values.address ?? defaultValues.address;
  const fetchAccountLocationsQuery = useFetchAccountLocationsQuery(
    selectedOrgId,
    accountId,
    { enabled: isShown(fields.address) && accountId > 0 },
  );
  const addressDropdown = LocationModel.buildAddressDropdown(
    fetchAccountLocationsQuery.data ?? [],
  );
  const isSavedAddress = addressDropdown.some((item) => item.value === address);

  const updateAddress = () => {
    setValue("address", LocationModel.getAddressLabel(getValues()), {
      shouldValidate: true,
    });
  };

  const clearAddressParts = () => {
    (["addressLine1", "addressLine2", "city", "state", "zip"] as const).forEach(
      (name) => setValue(name, ""),
    );
  };

  const handleAccountChange = () => {
    setValue("address", "");
    clearAddressParts();
  };

  useEffect(() => {
    if (!addressDropdown.length) return;

    clearAddressParts();

    if (addressDropdown.length === 1 && !address) {
      setValue("address", String(addressDropdown[0].value), {
        shouldValidate: true,
      });
    }
  }, [fetchAccountLocationsQuery.data]);

  const toggleCrewMember = (id: number) => {
    setIsCrewMissing(false);
    setCrewIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleValid = (data: JobFormData) => {
    if (isRequired(JOB_TYPE_FIELDS[data.type].crew) && crewIds.length === 0) {
      setIsCrewMissing(true);
      return;
    }

    const apiRecurrence = JobModel.getApiRecurrence(recurrenceRule, data.date);

    if (isShown(JOB_TYPE_FIELDS[data.type].recurrence) && !apiRecurrence) {
      setIsRecurrenceUnsupported(true);
      return;
    }

    handleEdit(
      JobModel.buildJob(
        { ...data, ...apiRecurrence },
        crewIds,
        references,
        job,
        assignmentRecurrence,
      ),
    );
  };

  const getAvailabilityLabel = (member: CrewMember) => {
    if (!selectedDay) return "";

    const hours = member.availability[selectedDay.format("ddd") as CrewDay];

    return hours
      ? `${JobModel.getTimeLabel(hours.start)} – ${JobModel.getTimeLabel(hours.end)}`
      : "Unavailable";
  };

  return (
    <Box
      component="form"
      id={id}
      noValidate
      onSubmit={handleSubmit(handleValid)}
    >
      <Grid container columnSpacing={2} rowSpacing={3}>
        <Grid size={12}>
          <Box
            className="dashboard-segmented"
            role="tablist"
            sx={{ width: "100%" }}
          >
            {SCHEDULE_CATEGORY_OPTIONS.map((option) => (
              <Box
                key={option.value}
                role="tab"
                aria-selected={category === option.value}
                aria-disabled={!!job}
                className={`dashboard-segmented-item${category === option.value ? " active" : ""}`}
                sx={{
                  flex: 1,
                  px: 1,
                  cursor: job ? "default" : "pointer",
                  opacity: job && category !== option.value ? 0.5 : 1,
                }}
                onClick={() => handleCategoryChange(option.value)}
              >
                {option.label}
              </Box>
            ))}
          </Box>
        </Grid>
        {category === ScheduleCategory.Event && (
          <Grid size={12}>
            <SelectBox
              label="Event Type"
              name="type"
              register={register}
              error={errors["type"]}
              dropdown={JOB_EVENT_TYPE_DROPDOWN}
              defaultValue={values.type}
              disabled={!!job}
              required
            />
          </Grid>
        )}
        {isShown(fields.title) && (
          <Grid size={12}>
            <TextField
              label="Title"
              name="title"
              register={register}
              error={errors["title"]}
              defaultValue={defaultValues.title}
              placeholder="e.g. Equipment pickup"
              maxLength={100}
              required={isRequired(fields.title)}
            />
          </Grid>
        )}
        {isShown(fields.customer) && (
          <Grid size={12}>
            <SelectBox
              label="Customer"
              name="accountId"
              register={register}
              error={errors["accountId"]}
              dropdown={[
                { key: "Select customer", value: 0 },
                ...accountDropdown,
              ]}
              defaultValue={defaultValues.accountId}
              onChange={handleAccountChange}
              required={isRequired(fields.customer)}
            />
          </Grid>
        )}
        {isShown(fields.address) &&
          (addressDropdown.length ? (
            <Grid size={12}>
              <SelectBox
                label="Service Address"
                name="address"
                error={errors["address"]}
                dropdown={[
                  { key: "Select address", value: "" },
                  ...(address && !isSavedAddress
                    ? [{ key: address, value: address }]
                    : []),
                  ...addressDropdown,
                ]}
                value={address}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxWidth: 0,
                      "& .MuiMenuItem-root": { whiteSpace: "normal" },
                    },
                  },
                }}
                sx={{
                  "& .MuiSelect-select": {
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                onChange={(event) =>
                  setValue("address", event.target.value, {
                    shouldValidate: true,
                  })
                }
                required={isRequired(fields.address)}
              />
            </Grid>
          ) : (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Address Line 1"
                  name="addressLine1"
                  register={register}
                  error={errors["addressLine1"] ?? errors["address"]}
                  defaultValue={defaultValues.addressLine1}
                  onChange={updateAddress}
                  maxLength={50}
                  required={isRequired(fields.address)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Address Line 2 (Optional)"
                  name="addressLine2"
                  register={register}
                  error={errors["addressLine2"]}
                  defaultValue={defaultValues.addressLine2}
                  onChange={updateAddress}
                  maxLength={50}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="City"
                  name="city"
                  register={register}
                  error={errors["city"]}
                  defaultValue={defaultValues.city}
                  onChange={updateAddress}
                  maxLength={50}
                  required={isRequired(fields.address)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <SelectBox
                  label={stateLabel}
                  name="state"
                  dropdown={statesDropdown}
                  error={errors["state"]}
                  value={values.state ?? defaultValues.state}
                  onChange={(event) => {
                    setValue("state", event.target.value, {
                      shouldValidate: true,
                    });
                    updateAddress();
                  }}
                  required={isRequired(fields.address)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label={zipLabel}
                  name="zip"
                  register={register}
                  error={errors["zip"]}
                  defaultValue={defaultValues.zip}
                  onChange={updateAddress}
                  maxLength={10}
                  required={isRequired(fields.address)}
                />
              </Grid>
            </>
          ))}
        <Grid size={{ xs: 12, sm: 4 }}>
          <DatePicker
            label="Date"
            name="date"
            control={control}
            error={errors["date"]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          {isCustomStartTime ? (
            <CustomTimeField
              label="Start Time"
              value={moment(startTime, JOB_TIME_FORMAT)}
              format={JOB_TIME_INPUT_FORMAT}
              ampm
              error={errors["startTime"]}
              onChange={(value) =>
                setValue("startTime", value.format(JOB_TIME_FORMAT), {
                  shouldValidate: true,
                })
              }
              onShowList={() => setIsCustomStartTime(false)}
            />
          ) : (
            <SelectBox
              label="Start Time"
              name="startTime"
              error={errors["startTime"]}
              dropdown={startTimeDropdown}
              value={startTime}
              onChange={(e) => handleStartTimeChange(e.target.value)}
              required
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          {isCustomDuration ? (
            <CustomTimeField
              label="Duration"
              value={moment().startOf("day").add(durationMinutes, "minutes")}
              format={JOB_DURATION_INPUT_FORMAT}
              ampm={false}
              error={errors["durationMinutes"]}
              onChange={(value) => {
                const minutes = value.hours() * 60 + value.minutes();
                if (minutes > 0) {
                  setValue("durationMinutes", minutes, {
                    shouldValidate: true,
                  });
                }
              }}
              onShowList={() => setIsCustomDuration(false)}
            />
          ) : (
            <SelectBox
              label="Duration"
              name="durationMinutes"
              error={errors["durationMinutes"]}
              dropdown={durationDropdown}
              value={durationMinutes}
              onChange={(e) => handleDurationChange(e.target.value)}
              required
            />
          )}
        </Grid>
        {isShown(fields.truck) && (
          <Grid size={12}>
            <SelectBox
              label="Assign Truck / Equipment"
              name="truckId"
              register={register}
              error={errors["truckId"]}
              dropdown={[
                { key: "Unassigned", value: JOB_UNASSIGNED_TRUCK },
                ...JobModel.buildTruckDropdown(trucks),
              ]}
              defaultValue={defaultValues.truckId}
              displayEmpty
              required={isRequired(fields.truck)}
            />
            {isRepeatingJob && values.truckId && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isTruckRecurring}
                    onChange={() => setIsTruckRecurring((prev) => !prev)}
                  />
                }
                label={
                  <Box
                    component="span"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <RepeatIcon
                      fontSize="small"
                      sx={{ color: "var(--dashboard-text-secondary)" }}
                    />
                    Repeat truck on future jobs
                  </Box>
                }
                sx={{
                  mt: errors["truckId"] ? 3 : 0.5,
                  "& .MuiFormControlLabel-label": { fontSize: 14 },
                }}
              />
            )}
          </Grid>
        )}
        <Grid size={12}>
          <Typography sx={{ mb: 0.5 }}>
            Assign Crew
            {isRequired(fields.crew) && (
              <Box component="span" sx={{ color: theme.palette.error.main }}>
                &nbsp;*
              </Box>
            )}
          </Typography>
          <Box
            sx={{
              maxHeight: 220,
              overflowY: "auto",
              border: "1px solid var(--dashboard-border-divider)",
              borderRadius: "var(--dashboard-radius-control)",
            }}
          >
            {isRepeatingJob && crew.length > 0 && (
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: "var(--dashboard-surface)",
                  borderBottom: "1px solid var(--dashboard-border-row)",
                }}
              >
                <Tooltip title="Repeat on future jobs">
                  <Box
                    sx={{
                      width: 38,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <RepeatIcon
                      fontSize="small"
                      sx={{ color: "var(--dashboard-text-secondary)" }}
                    />
                  </Box>
                </Tooltip>
              </Box>
            )}
            {crew.map((member) => (
              <Box
                key={member.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 1.5,
                  "&:not(:last-of-type)": {
                    borderBottom: "1px solid var(--dashboard-border-row)",
                  },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={crewIds.includes(member.id)}
                      onChange={() => toggleCrewMember(member.id)}
                    />
                  }
                  label={`${member.firstName} ${member.lastName}`}
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": { fontSize: 14 },
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <Typography className="dashboard-helper-text" noWrap>
                    {CREW_ROLE_LABELS[member.roleCode]} ·{" "}
                    {getAvailabilityLabel(member)}
                  </Typography>
                  {isRepeatingJob && (
                    <Checkbox
                      size="small"
                      checked={
                        crewIds.includes(member.id) &&
                        !oneTimeCrewIds.includes(member.id)
                      }
                      disabled={!crewIds.includes(member.id)}
                      onChange={() => toggleCrewRecurrence(member.id)}
                      inputProps={{
                        "aria-label": `Repeat ${member.firstName} ${member.lastName} on future jobs`,
                      }}
                    />
                  )}
                </Box>
              </Box>
            ))}
            {crew.length === 0 && (
              <Typography className="dashboard-helper-text" sx={{ p: 1.5 }}>
                No crew members found
              </Typography>
            )}
          </Box>
          {isCrewMissing && (
            <FormHelperText error>
              Select at least one crew member
            </FormHelperText>
          )}
        </Grid>
        {conflicts.length > 0 && (
          <Grid size={12}>
            <Alert severity="warning">
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                Conflicts
              </Typography>
              {conflicts.map((conflict, index) => (
                <Typography key={`${conflict}-${index}`} sx={{ fontSize: 13 }}>
                  • {conflict}
                </Typography>
              ))}
            </Alert>
          </Grid>
        )}
        {isShown(fields.recurrence) && (
          <Grid size={12}>
            <SelectBox
              label="Repeat"
              name="recurrence"
              dropdown={recurrenceDropdown}
              value={recurrenceLabel}
              onChange={(e) => handleRecurrenceChange(e.target.value)}
              required
            />
            {isRecurrenceUnsupported && (
              <FormHelperText error>
                {RECURRENCE_UNSUPPORTED_MESSAGE}
              </FormHelperText>
            )}
            {isCustomRecurrenceOpen && (
              <CustomRecurrenceDialog
                open={isCustomRecurrenceOpen}
                date={recurrenceDate}
                rule={
                  recurrenceRule ??
                  JobModel.getDefaultRecurrenceRule(recurrenceDate)
                }
                onClose={() => setIsCustomRecurrenceOpen(false)}
                onDone={(rule) => {
                  updateRecurrenceRule(rule);
                  setIsCustomRecurrenceOpen(false);
                }}
              />
            )}
          </Grid>
        )}
        {job && (
          <Grid size={12}>
            <SelectBox
              label="Status"
              name="status"
              register={register}
              error={errors["status"]}
              dropdown={JOB_STATUS_DROPDOWN}
              defaultValue={defaultValues.status}
              required
            />
          </Grid>
        )}
        <Grid size={12}>
          <TextField
            label="Notes"
            name="notes"
            register={register}
            error={errors["notes"]}
            defaultValue={defaultValues.notes}
            placeholder="Optional notes for crew"
            maxLength={500}
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
